/** React context and hook for accessing the SynthController.
 *
 * Provides the synth instance to all child components and handles
 * initialization on first user gesture. Persists settings to localStorage.
 * ADSR and filter can be per-layer or linked (changing one affects all).
 */

import type { ReactNode } from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { initAudio } from "../initAudio.ts";
import type { ArpParams } from "../lib/Arpeggiator.ts";
import { Arpeggiator, DEFAULT_ARP } from "../lib/Arpeggiator.ts";
import type { ChorusParams } from "../lib/Chorus.ts";
import { DEFAULT_CHORUS } from "../lib/Chorus.ts";
import { justIntonationCents } from "../lib/noteUtils.ts";
import type { SynthPreset } from "../lib/presets.ts";
import { SynthController } from "../lib/SynthController.ts";
import type {
	PersistedSequencerMeasures,
	PersistedSequencerNotes,
	PersistedState,
	PersistedSynthInstance,
	TuningState,
} from "../lib/stateStorage.ts";
import {
	loadState,
	saveArp,
	saveChorus,
	saveDelay,
	saveLimiter,
	saveLinked,
	saveMasterBusGain,
	saveParams,
	saveSequencer,
	saveSynthInstances,
	saveTuning,
	stateToShareURL,
} from "../lib/stateStorage.ts";
import { trace } from "../lib/synthTracer.ts";
import type {
	ADSRParams,
	DelayParams,
	FilterParams,
	LimiterParams,
	MidiChannel,
	OscillatorLayer,
	SynthParams,
	WaveformType,
} from "../types/synth.ts";
import {
	DEFAULT_DELAY,
	DEFAULT_LIMITER,
	DEFAULT_PARAMS,
	MAX_SYNTHS,
} from "../types/synth.ts";

/** Shape of the synth context value. */
export type SynthContextValue = {
	readonly synth: SynthController | null;
	/** The underlying AudioContext — available after init. */
	readonly audioContext: AudioContext | null;
	readonly isReady: boolean;
	readonly isLoading: boolean;
	readonly error: string | null;
	readonly params: SynthParams;
	readonly activeNotes: ReadonlySet<number>;
	readonly tuning: TuningState;
	readonly adsrLinked: boolean;
	readonly filterLinked: boolean;
	/** Master bus AnalyserNode for waveform visualization (all synths combined). */
	readonly masterAnalyser: AnalyserNode | null;
	readonly init: () => Promise<void>;
	readonly noteOn: (note: number) => void;
	readonly noteOff: (note: number) => void;
	readonly setADSR: (adsr: Partial<ADSRParams>) => void;
	readonly setFilter: (filter: Partial<FilterParams>) => void;
	/** Set the master bus gain (global output volume for all synths). */
	readonly setMasterBusGain: (gain: number) => void;
	/** Master bus gain value (0–1). */
	readonly masterBusGain: number;
	readonly setTempo: (bpm: number) => void;
	readonly setTuning: (tuning: Partial<TuningState>) => void;
	readonly setOscillatorLayers: (layers: readonly OscillatorLayer[]) => void;
	readonly updateLayer: (
		layerId: string,
		update: Partial<OscillatorLayer>,
	) => void;
	readonly updateLayerADSR: (
		layerId: string,
		adsr: Partial<ADSRParams>,
	) => void;
	readonly updateLayerFilter: (
		layerId: string,
		filter: Partial<FilterParams>,
	) => void;
	readonly setAdsrLinked: (linked: boolean) => void;
	readonly setFilterLinked: (linked: boolean) => void;
	readonly setDelay: (update: Partial<DelayParams>) => void;
	readonly delayParams: DelayParams;
	readonly setLimiter: (update: Partial<LimiterParams>) => void;
	readonly limiterParams: LimiterParams;
	/** Arpeggiator parameters. */
	readonly arpParams: ArpParams;
	/** Update arpeggiator parameters. */
	readonly setArp: (update: Partial<ArpParams>) => void;
	/** Chorus effect parameters. */
	readonly chorusParams: ChorusParams;
	/** Update chorus parameters. */
	readonly setChorus: (update: Partial<ChorusParams>) => void;
	readonly allNotesOff: () => void;
	readonly panic: () => void;
	// Multi-synth management
	readonly synthInstances: readonly PersistedSynthInstance[];
	readonly activeSynthId: string;
	readonly addSynth: () => void;
	readonly removeSynth: (id: string) => void;
	readonly selectSynth: (id: string) => void;
	readonly renameSynth: (id: string, name: string) => void;
	readonly setSynthMidiChannel: (id: string, channel: MidiChannel) => void;
	/** Set the volume for a specific synth instance (from mixer). */
	readonly setSynthVolume: (id: string, gain: number) => void;
	/** Set the pan for a specific synth instance (from mixer). */
	readonly setSynthPan: (id: string, pan: number) => void;
	/** Toggle mute for a specific synth instance. */
	readonly setSynthMuted: (id: string, muted: boolean) => void;
	/** Toggle solo for a specific synth instance. */
	readonly setSynthSolo: (id: string, solo: boolean) => void;
	/** Route a noteOn to all synths matching the given MIDI channel. */
	readonly midiNoteOn: (note: number, channel: number) => void;
	/** Route a noteOff to all synths matching the given MIDI channel. */
	readonly midiNoteOff: (note: number, channel: number) => void;
	/** Trigger a noteOn on a specific synth instance by ID (for sequencer). */
	readonly synthNoteOn: (
		synthId: string,
		note: number,
		velocity?: number,
	) => void;
	/** Trigger a noteOff on a specific synth instance by ID (for sequencer). */
	readonly synthNoteOff: (synthId: string, note: number) => void;
	/** Get a shareable URL encoding the current state. */
	readonly getShareURL: () => string;
	/** Load a synth preset (layers + delay) into the active synth. */
	readonly loadPreset: (preset: SynthPreset) => void;
	/** Initial sequencer notes loaded from persisted state. */
	readonly initialSequencerNotes: PersistedSequencerNotes;
	/** Initial sequencer measures loaded from persisted state (per-synth). */
	readonly initialSequencerMeasures: PersistedSequencerMeasures;
	/** Persist updated sequencer state (notes per synth + measures per synth). */
	readonly saveSequencerState: (
		notes: PersistedSequencerNotes,
		measures: PersistedSequencerMeasures,
	) => void;
	/** Randomize all synth parameters within musically useful ranges. */
	readonly randomizeParams: () => void;
	/** Export current synth config as a downloadable JSON file. */
	readonly exportPreset: () => void;
	/** Import a synth preset from a JSON file. */
	readonly importPreset: (file: File) => void;
};

const SynthContext = createContext<SynthContextValue | null>(null);

/** Hook to access the synth context. Must be used within a SynthProvider. */
export function useSynth(): SynthContextValue {
	const ctx = useContext(SynthContext);
	if (!ctx) {
		throw new Error("useSynth must be used within a <SynthProvider>");
	}
	return ctx;
}

/** Props for the SynthProvider component. */
export type SynthProviderProps = {
	readonly children: ReactNode;
};

/** Provides the SynthController instance and state to the component tree. */
export function SynthProvider({ children }: SynthProviderProps) {
	const synthRef = useRef<SynthController | null>(null);
	const synthMapRef = useRef<Map<string, SynthController>>(new Map());
	const persistedRef = useRef<PersistedState>(loadState());
	const audioEnvRef = useRef<Awaited<ReturnType<typeof initAudio>> | null>(
		null,
	);
	/** Master bus GainNode — all synths route through this before reaching destination. */
	const masterBusNodeRef = useRef<GainNode | null>(null);
	/** Master bus analyser — used for waveform visualization. */
	const masterBusAnalyserRef = useRef<AnalyserNode | null>(null);

	const [isReady, setIsReady] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [params, setParams] = useState<SynthParams>(
		persistedRef.current.params,
	);
	const [tuning, setTuningState] = useState<TuningState>(
		persistedRef.current.tuning,
	);
	const [adsrLinked, setAdsrLinkedState] = useState(
		persistedRef.current.adsrLinked,
	);
	const [filterLinked, setFilterLinkedState] = useState(
		persistedRef.current.filterLinked,
	);
	const [activeNotes, setActiveNotes] = useState<ReadonlySet<number>>(
		new Set(),
	);
	/** Ref that always reflects the current active notes set (for use in callbacks). */
	const activeNotesRef = useRef<Set<number>>(new Set());
	const [delayParams, setDelayParams] = useState<DelayParams>(
		persistedRef.current.delay,
	);
	const [limiterParams, setLimiterParams] = useState<LimiterParams>(
		persistedRef.current.limiter,
	);
	const [arpParams, setArpParamsState] = useState<ArpParams>(
		persistedRef.current.arp ?? DEFAULT_ARP,
	);
	/** Per-synth arpeggiator instances — keyed by synth instance ID. */
	const arpMapRef = useRef<Map<string, Arpeggiator>>(new Map());
	/** Get or create arpeggiator for a synth instance. */
	const getArp = useCallback((synthId: string): Arpeggiator => {
		let arp = arpMapRef.current.get(synthId);
		if (!arp) {
			arp = new Arpeggiator();
			arpMapRef.current.set(synthId, arp);
		}
		return arp;
	}, []);
	const [chorusParams, setChorusParamsState] = useState<ChorusParams>(
		persistedRef.current.chorus ?? DEFAULT_CHORUS,
	);
	const [synthInstances, setSynthInstances] = useState<
		readonly PersistedSynthInstance[]
	>(persistedRef.current.synthInstances);
	const [activeSynthId, setActiveSynthId] = useState<string>(
		persistedRef.current.activeSynthId,
	);
	const [masterBusGainValue, setMasterBusGainValue] = useState<number>(
		persistedRef.current.masterBusGain,
	);

	const synthCounter = useRef(persistedRef.current.synthInstances.length);

	const initCalledRef = useRef(false);

	const init = useCallback(async () => {
		if (initCalledRef.current) return;
		initCalledRef.current = true;

		setIsLoading(true);
		setError(null);

		try {
			const env = await initAudio();
			audioEnvRef.current = env;

			// Create master bus: GainNode → destination + analyser
			const masterBus = env.context.createGain();
			masterBus.gain.value = persistedRef.current.masterBusGain;
			const masterAnalyser = env.context.createAnalyser();
			masterAnalyser.fftSize = 2048;
			masterBus.connect(env.context.destination);
			masterBus.connect(masterAnalyser);
			masterBusNodeRef.current = masterBus;
			masterBusAnalyserRef.current = masterAnalyser;

			// Create SynthController for each persisted instance (route to master bus)
			const instances = persistedRef.current.synthInstances;
			for (const inst of instances) {
				const synth = new SynthController(env, masterBus);
				synth.setMasterGain(inst.params.masterGain);
				synth.setMasterPan(inst.params.pan ?? 0);
				synth.setCentOffset(persistedRef.current.tuning.centOffset);
				synth.setOscillatorLayers(inst.params.oscillatorLayers);
				synth.setTempo(inst.params.tempo);
				synth.setDelay(inst.delay);
				synth.setLimiter(inst.limiter);
				synth.setChorus(inst.chorus ?? {});
				synthMapRef.current.set(inst.id, synth);
			}

			// Apply mute/solo state
			const anySoloed = instances.some((i) => i.solo);
			for (const inst of instances) {
				const synth = synthMapRef.current.get(inst.id);
				if (synth?.isReady) {
					synth.setMuted(inst.muted || (anySoloed && !inst.solo));
				}
			}

			// Set active synth ref
			const activeId = persistedRef.current.activeSynthId;
			synthRef.current = synthMapRef.current.get(activeId) ?? null;

			// Wire per-synth arpeggiators
			for (const inst of instances) {
				const arp = getArp(inst.id);
				const instArpState =
					inst.arp ?? persistedRef.current.arp ?? DEFAULT_ARP;
				arp.setParams(instArpState);
				arp.setTempo(persistedRef.current.params.tempo);
				const synthForArp = synthMapRef.current.get(inst.id);
				arp.setCallback({
					noteOn: (note: number) => {
						if (synthForArp?.isReady) synthForArp.noteOn(note);
					},
					noteOff: (note: number) => {
						if (synthForArp?.isReady) synthForArp.noteOff(note);
					},
				});
			}

			// Set active synth arp params as the UI state
			const activeInst = instances.find((i) => i.id === activeId);
			setArpParamsState(
				activeInst?.arp ?? persistedRef.current.arp ?? DEFAULT_ARP,
			);

			trace("hook", "init:success", {
				ctxState: env.context.state,
				sampleRate: env.context.sampleRate,
				synthCount: instances.length,
			});

			setIsReady(true);
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to initialize synth";
			setError(message);
			initCalledRef.current = false;
			trace("hook", "init:error", { message });
			console.error("Synth init failed:", err);
		} finally {
			setIsLoading(false);
		}
	}, [getArp]);

	useEffect(() => {
		init();
	}, [init]);

	const noteOn = useCallback(
		(note: number) => {
			const synth = synthRef.current;
			const ready = synth?.isReady ?? false;
			trace("hook", "noteOn", { note, synthExists: !!synth, ready });
			if (!synth?.isReady) return;

			// Route through per-synth arpeggiator if enabled
			const arp = getArp(activeSynthId);
			if (arp.currentParams.enabled) {
				arp.noteOn(note);
			} else {
				// Apply just intonation offset when cleanChords is enabled
				const cleanChords = persistedRef.current.tuning.cleanChords ?? false;
				let extraCents = 0;
				if (cleanChords && activeNotesRef.current.size > 0) {
					// Root = the lowest currently-playing note
					const root = Math.min(...activeNotesRef.current);
					extraCents = justIntonationCents(note, root);
				}
				synth.noteOn(note, 127, extraCents);
			}
			activeNotesRef.current.add(note);
			setActiveNotes((prev) => {
				const next = new Set(prev);
				next.add(note);
				return next;
			});
		},
		[activeSynthId, getArp],
	);

	const noteOff = useCallback(
		(note: number) => {
			const synth = synthRef.current;
			const ready = synth?.isReady ?? false;
			trace("hook", "noteOff", { note, synthExists: !!synth, ready });
			if (!synth?.isReady) return;

			// Route through per-synth arpeggiator if enabled
			const arp = getArp(activeSynthId);
			if (arp.currentParams.enabled) {
				arp.noteOff(note);
			} else {
				synth.noteOff(note);
			}
			activeNotesRef.current.delete(note);
			setActiveNotes((prev) => {
				const next = new Set(prev);
				next.delete(note);
				return next;
			});
		},
		[activeSynthId, getArp],
	);

	/** Set ADSR on ALL layers — used by MIDI CC mapping. */
	const setADSR = useCallback((adsr: Partial<ADSRParams>) => {
		const synth = synthRef.current;
		if (!synth?.isReady) return;
		synth.setADSR(adsr);
		setParams((prev) => {
			const layers = prev.oscillatorLayers.map((l) => ({
				...l,
				adsr: { ...l.adsr, ...adsr },
			}));
			const next = { ...prev, oscillatorLayers: layers };
			persistedRef.current = saveParams(next, persistedRef.current);
			return next;
		});
	}, []);

	/** Set filter on ALL layers — used by MIDI CC mapping. */
	const setFilter = useCallback((filter: Partial<FilterParams>) => {
		const synth = synthRef.current;
		if (!synth?.isReady) return;
		synth.setFilter(filter);
		setParams((prev) => {
			const layers = prev.oscillatorLayers.map((l) => ({
				...l,
				filter: { ...l.filter, ...filter },
			}));
			const next = { ...prev, oscillatorLayers: layers };
			persistedRef.current = saveParams(next, persistedRef.current);
			return next;
		});
	}, []);

	/** Set the master bus gain (global output volume for all synths). */
	const setMasterBusGain = useCallback((gain: number) => {
		const busNode = masterBusNodeRef.current;
		if (busNode) {
			busNode.gain.cancelScheduledValues(0);
			busNode.gain.setValueAtTime(gain, busNode.context.currentTime);
		}
		setMasterBusGainValue(gain);
		persistedRef.current = saveMasterBusGain(gain, persistedRef.current);
	}, []);

	const setTempo = useCallback((bpm: number) => {
		// Apply tempo to ALL synth instances (global parameter)
		for (const synth of synthMapRef.current.values()) {
			if (synth.isReady) {
				synth.setTempo(bpm);
			}
		}
		// Also update all arpeggiator tempos
		for (const arp of arpMapRef.current.values()) {
			arp.setTempo(bpm);
		}
		setParams((prev) => {
			const next = { ...prev, tempo: bpm };
			persistedRef.current = saveParams(next, persistedRef.current);
			return next;
		});
	}, []);

	const setOscillatorLayers = useCallback(
		(layers: readonly OscillatorLayer[]) => {
			const synth = synthRef.current;
			if (!synth?.isReady) return;
			synth.setOscillatorLayers(layers);
			setParams((prev) => {
				const next = { ...prev, oscillatorLayers: layers };
				persistedRef.current = saveParams(next, persistedRef.current);
				return next;
			});
		},
		[],
	);

	/** Update non-ADSR/filter layer properties (waveform, pitch, gain). */
	const updateLayer = useCallback(
		(layerId: string, update: Partial<OscillatorLayer>) => {
			const synth = synthRef.current;
			if (!synth?.isReady) return;
			synth.updateLayer(layerId, update);
			setParams((prev) => {
				const layers = prev.oscillatorLayers.map((l) =>
					l.id === layerId ? { ...l, ...update } : l,
				);
				const next = { ...prev, oscillatorLayers: layers };
				persistedRef.current = saveParams(next, persistedRef.current);
				return next;
			});
		},
		[],
	);

	/** Update a layer's ADSR — if linked, propagate to all layers. */
	const updateLayerADSR = useCallback(
		(layerId: string, adsr: Partial<ADSRParams>) => {
			const synth = synthRef.current;
			if (!synth?.isReady) return;

			const linked = persistedRef.current.adsrLinked;
			if (linked) {
				// Update all layers
				synth.setADSR(adsr);
				setParams((prev) => {
					const layers = prev.oscillatorLayers.map((l) => ({
						...l,
						adsr: { ...l.adsr, ...adsr },
					}));
					const next = { ...prev, oscillatorLayers: layers };
					persistedRef.current = saveParams(next, persistedRef.current);
					return next;
				});
			} else {
				// Update only the target layer
				synth.setLayerADSR(layerId, adsr);
				setParams((prev) => {
					const layers = prev.oscillatorLayers.map((l) =>
						l.id === layerId ? { ...l, adsr: { ...l.adsr, ...adsr } } : l,
					);
					const next = { ...prev, oscillatorLayers: layers };
					persistedRef.current = saveParams(next, persistedRef.current);
					return next;
				});
			}
		},
		[],
	);

	/** Update a layer's filter — if linked, propagate to all layers. */
	const updateLayerFilter = useCallback(
		(layerId: string, filter: Partial<FilterParams>) => {
			const synth = synthRef.current;
			if (!synth?.isReady) return;

			const linked = persistedRef.current.filterLinked;
			if (linked) {
				synth.setFilter(filter);
				setParams((prev) => {
					const layers = prev.oscillatorLayers.map((l) => ({
						...l,
						filter: { ...l.filter, ...filter },
					}));
					const next = { ...prev, oscillatorLayers: layers };
					persistedRef.current = saveParams(next, persistedRef.current);
					return next;
				});
			} else {
				synth.setLayerFilter(layerId, filter);
				setParams((prev) => {
					const layers = prev.oscillatorLayers.map((l) =>
						l.id === layerId ? { ...l, filter: { ...l.filter, ...filter } } : l,
					);
					const next = { ...prev, oscillatorLayers: layers };
					persistedRef.current = saveParams(next, persistedRef.current);
					return next;
				});
			}
		},
		[],
	);

	const setAdsrLinked = useCallback((linked: boolean) => {
		setAdsrLinkedState(linked);
		persistedRef.current = saveLinked(
			linked,
			persistedRef.current.filterLinked,
			persistedRef.current,
		);
	}, []);

	const setFilterLinked = useCallback((linked: boolean) => {
		setFilterLinkedState(linked);
		persistedRef.current = saveLinked(
			persistedRef.current.adsrLinked,
			linked,
			persistedRef.current,
		);
	}, []);

	const setDelay = useCallback((update: Partial<DelayParams>) => {
		const synth = synthRef.current;
		if (synth?.isReady) {
			synth.setDelay(update);
		}
		setDelayParams((prev) => {
			const next = { ...prev, ...update };
			persistedRef.current = saveDelay(next, persistedRef.current);
			return next;
		});
	}, []);

	const setLimiter = useCallback((update: Partial<LimiterParams>) => {
		const synth = synthRef.current;
		if (synth?.isReady) {
			synth.setLimiter(update);
		}
		setLimiterParams((prev) => {
			const next = { ...prev, ...update };
			persistedRef.current = saveLimiter(next, persistedRef.current);
			return next;
		});
	}, []);

	/** Update arpeggiator parameters for the active synth and persist. */
	const setArp = useCallback(
		(update: Partial<ArpParams>) => {
			const arp = getArp(activeSynthId);
			arp.setParams(update);
			setArpParamsState((prev) => {
				const next = { ...prev, ...update };
				persistedRef.current = saveArp(next, persistedRef.current);
				// Also persist per-instance
				setSynthInstances((prevInst) => {
					const updated = prevInst.map((inst) =>
						inst.id === activeSynthId ? { ...inst, arp: next } : inst,
					);
					persistedRef.current = saveSynthInstances(
						updated,
						activeSynthId,
						persistedRef.current,
					);
					return updated;
				});
				return next;
			});
		},
		[activeSynthId, getArp],
	);

	/** Update chorus parameters and persist. */
	const setChorus = useCallback((update: Partial<ChorusParams>) => {
		const synth = synthRef.current;
		if (synth?.isReady) {
			synth.setChorus(update);
		}
		setChorusParamsState((prev) => {
			const next = { ...prev, ...update };
			persistedRef.current = saveChorus(next, persistedRef.current);
			return next;
		});
	}, []);

	const setTuning = useCallback((partial: Partial<TuningState>) => {
		setTuningState((prev) => {
			const next = { ...prev, ...partial };
			persistedRef.current = saveTuning(next, persistedRef.current);
			// Apply cent offset to the synth controller in real time
			if (partial.centOffset !== undefined) {
				synthRef.current?.setCentOffset(partial.centOffset);
			}
			return next;
		});
	}, []);

	const allNotesOff = useCallback(() => {
		getArp(activeSynthId).stop();
		const synth = synthRef.current;
		if (!synth?.isReady) return;
		const released = synth.allNotesOff();
		if (released.length > 0) {
			activeNotesRef.current.clear();
			setActiveNotes(new Set());
		}
	}, [activeSynthId, getArp]);

	const panic = useCallback(() => {
		// Stop all arpeggiators
		for (const arp of arpMapRef.current.values()) {
			arp.stop();
		}
		// Panic all synths, not just the active one
		for (const synth of synthMapRef.current.values()) {
			if (synth.isReady) synth.panic();
		}
		activeNotesRef.current.clear();
		setActiveNotes(new Set());
		trace("hook", "panic", {});
	}, []);

	// Also update the instance in synthInstances
	// Sync params changes back to the active synthInstance
	useEffect(() => {
		setSynthInstances((prev) => {
			const updated = prev.map((inst) =>
				inst.id === activeSynthId
					? {
							...inst,
							params,
							delay: delayParams,
							limiter: limiterParams,
							chorus: chorusParams,
							arp: arpParams,
						}
					: inst,
			);
			persistedRef.current = saveSynthInstances(
				updated,
				activeSynthId,
				persistedRef.current,
			);
			return updated;
		});
	}, [
		params,
		delayParams,
		limiterParams,
		chorusParams,
		arpParams,
		activeSynthId,
	]);

	// ---- Multi-synth management ----

	const addSynth = useCallback(() => {
		if (synthInstances.length >= MAX_SYNTHS) return;
		const env = audioEnvRef.current;
		if (!env) return;

		synthCounter.current += 1;
		const id = `synth-${synthCounter.current}-${Date.now()}`;
		const name = `Synth ${synthCounter.current}`;
		const newInstance: PersistedSynthInstance = {
			id,
			name,
			midiChannel: "all",
			params: DEFAULT_PARAMS,
			delay: DEFAULT_DELAY,
			limiter: DEFAULT_LIMITER,
			chorus: DEFAULT_CHORUS,
			muted: false,
			solo: false,
			arp: DEFAULT_ARP,
		};

		// Create new SynthController (route through master bus)
		const masterBus = masterBusNodeRef.current ?? undefined;
		const synth = new SynthController(env, masterBus);
		synth.setTempo(DEFAULT_PARAMS.tempo);
		synthMapRef.current.set(id, synth);

		// Create arpeggiator for the new synth
		const arp = getArp(id);
		arp.setTempo(DEFAULT_PARAMS.tempo);
		arp.setCallback({
			noteOn: (note: number) => {
				if (synth.isReady) synth.noteOn(note);
			},
			noteOff: (note: number) => {
				if (synth.isReady) synth.noteOff(note);
			},
		});

		setSynthInstances((prev) => {
			const next = [...prev, newInstance];
			persistedRef.current = saveSynthInstances(
				next,
				activeSynthId,
				persistedRef.current,
			);
			return next;
		});
	}, [synthInstances.length, activeSynthId, getArp]);

	const removeSynth = useCallback(
		(id: string) => {
			if (synthInstances.length <= 1) return;

			// Destroy the controller
			const synth = synthMapRef.current.get(id);
			if (synth) {
				synth.panic();
				synth.destroy();
				synthMapRef.current.delete(id);
			}

			// Destroy the arpeggiator for this synth
			const arp = arpMapRef.current.get(id);
			if (arp) {
				arp.destroy();
				arpMapRef.current.delete(id);
			}

			setSynthInstances((prev) => {
				const next = prev.filter((inst) => inst.id !== id);
				const newActiveId =
					id === activeSynthId ? (next[0]?.id ?? activeSynthId) : activeSynthId;

				if (id === activeSynthId && next[0]) {
					// Switch to first remaining synth
					const newSynth = synthMapRef.current.get(newActiveId) ?? null;
					synthRef.current = newSynth;
					setActiveSynthId(newActiveId);
					if (next[0]) {
						setParams(next[0].params);
						setDelayParams(next[0].delay);
						setLimiterParams(next[0].limiter);
						setChorusParamsState(next[0].chorus ?? DEFAULT_CHORUS);
						setArpParamsState(next[0].arp ?? DEFAULT_ARP);
					}
				}

				persistedRef.current = saveSynthInstances(
					next,
					newActiveId,
					persistedRef.current,
				);
				return next;
			});
		},
		[synthInstances.length, activeSynthId],
	);

	const selectSynth = useCallback(
		(id: string) => {
			if (id === activeSynthId) return;

			// Switch to new synth
			const newSynth = synthMapRef.current.get(id) ?? null;
			synthRef.current = newSynth;
			setActiveSynthId(id);
			setActiveNotes(new Set());

			// Load the selected synth's params (preserve global tempo)
			const inst = synthInstances.find((i) => i.id === id);
			if (inst) {
				setParams((prev) => ({
					...inst.params,
					tempo: prev.tempo,
				}));
				setDelayParams(inst.delay);
				setLimiterParams(inst.limiter);
				setChorusParamsState(inst.chorus ?? DEFAULT_CHORUS);
				setArpParamsState(inst.arp ?? DEFAULT_ARP);
			}

			persistedRef.current = saveSynthInstances(
				persistedRef.current.synthInstances,
				id,
				persistedRef.current,
			);
		},
		[activeSynthId, synthInstances],
	);

	const renameSynth = useCallback(
		(id: string, name: string) => {
			setSynthInstances((prev) => {
				const next = prev.map((inst) =>
					inst.id === id ? { ...inst, name } : inst,
				);
				persistedRef.current = saveSynthInstances(
					next,
					activeSynthId,
					persistedRef.current,
				);
				return next;
			});
		},
		[activeSynthId],
	);

	const setSynthMidiChannel = useCallback(
		(id: string, channel: MidiChannel) => {
			setSynthInstances((prev) => {
				const next = prev.map((inst) =>
					inst.id === id ? { ...inst, midiChannel: channel } : inst,
				);
				persistedRef.current = saveSynthInstances(
					next,
					activeSynthId,
					persistedRef.current,
				);
				return next;
			});
		},
		[activeSynthId],
	);

	/** Set the volume for a specific synth instance. */
	const setSynthVolume = useCallback(
		(id: string, gain: number) => {
			const synth = synthMapRef.current.get(id);
			if (synth?.isReady) {
				synth.setMasterGain(gain);
			}
			// If it's the active synth, also update the local params state
			if (id === activeSynthId) {
				setParams((prev) => {
					const next = { ...prev, masterGain: gain };
					persistedRef.current = saveParams(next, persistedRef.current);
					return next;
				});
			}
			// Update the instance directly
			setSynthInstances((prev) => {
				const next = prev.map((inst) =>
					inst.id === id
						? { ...inst, params: { ...inst.params, masterGain: gain } }
						: inst,
				);
				persistedRef.current = saveSynthInstances(
					next,
					activeSynthId,
					persistedRef.current,
				);
				return next;
			});
		},
		[activeSynthId],
	);

	/** Set the pan for a specific synth instance. */
	const setSynthPan = useCallback(
		(id: string, pan: number) => {
			const synth = synthMapRef.current.get(id);
			if (synth?.isReady) {
				synth.setMasterPan(pan);
			}
			// If it's the active synth, also update the local params state
			if (id === activeSynthId) {
				setParams((prev) => {
					const next = { ...prev, pan };
					persistedRef.current = saveParams(next, persistedRef.current);
					return next;
				});
			}
			// Update the instance directly
			setSynthInstances((prev) => {
				const next = prev.map((inst) =>
					inst.id === id ? { ...inst, params: { ...inst.params, pan } } : inst,
				);
				persistedRef.current = saveSynthInstances(
					next,
					activeSynthId,
					persistedRef.current,
				);
				return next;
			});
		},
		[activeSynthId],
	);

	/** Recompute the effective mute state for all synths based on mute/solo flags. */
	const recomputeMuteState = useCallback(
		(instances: readonly PersistedSynthInstance[]) => {
			const anySoloed = instances.some((i) => i.solo);
			for (const inst of instances) {
				const synth = synthMapRef.current.get(inst.id);
				if (!synth?.isReady) continue;
				// Muted if: explicitly muted, OR (some synth is soloed and this one isn't)
				const effectivelyMuted = inst.muted || (anySoloed && !inst.solo);
				synth.setMuted(effectivelyMuted);
			}
		},
		[],
	);

	/** Toggle mute for a specific synth instance. */
	const setSynthMuted = useCallback(
		(id: string, muted: boolean) => {
			setSynthInstances((prev) => {
				const next = prev.map((inst) =>
					inst.id === id ? { ...inst, muted } : inst,
				);
				recomputeMuteState(next);
				persistedRef.current = saveSynthInstances(
					next,
					activeSynthId,
					persistedRef.current,
				);
				return next;
			});
		},
		[activeSynthId, recomputeMuteState],
	);

	/** Toggle solo for a specific synth instance. */
	const setSynthSolo = useCallback(
		(id: string, solo: boolean) => {
			setSynthInstances((prev) => {
				const next = prev.map((inst) =>
					inst.id === id ? { ...inst, solo } : inst,
				);
				recomputeMuteState(next);
				persistedRef.current = saveSynthInstances(
					next,
					activeSynthId,
					persistedRef.current,
				);
				return next;
			});
		},
		[activeSynthId, recomputeMuteState],
	);

	/** Route noteOn to all synths matching the MIDI channel.
	 *  Notes are routed through the per-synth arpeggiator when enabled.
	 */
	const midiNoteOn = useCallback(
		(note: number, channel: number) => {
			for (const inst of synthInstances) {
				if (inst.midiChannel === "all" || inst.midiChannel === channel) {
					// Route through per-synth arpeggiator when enabled
					const arp = getArp(inst.id);
					if (arp.currentParams.enabled) {
						arp.noteOn(note);
					} else {
						const synth = synthMapRef.current.get(inst.id);
						if (synth?.isReady) {
							synth.noteOn(note);
						}
					}
				}
			}
			// Update active notes UI for the active synth
			const activeInst = synthInstances.find((i) => i.id === activeSynthId);
			if (
				activeInst &&
				(activeInst.midiChannel === "all" || activeInst.midiChannel === channel)
			) {
				setActiveNotes((prev) => {
					const next = new Set(prev);
					next.add(note);
					return next;
				});
			}
		},
		[synthInstances, activeSynthId, getArp],
	);

	/** Route noteOff to all synths matching the MIDI channel.
	 *  Notes are routed through the per-synth arpeggiator when enabled.
	 */
	const midiNoteOff = useCallback(
		(note: number, channel: number) => {
			for (const inst of synthInstances) {
				if (inst.midiChannel === "all" || inst.midiChannel === channel) {
					// Route through per-synth arpeggiator when enabled
					const arp = getArp(inst.id);
					if (arp.currentParams.enabled) {
						arp.noteOff(note);
					} else {
						const synth = synthMapRef.current.get(inst.id);
						if (synth?.isReady) {
							synth.noteOff(note);
						}
					}
				}
			}
			const activeInst = synthInstances.find((i) => i.id === activeSynthId);
			if (
				activeInst &&
				(activeInst.midiChannel === "all" || activeInst.midiChannel === channel)
			) {
				setActiveNotes((prev) => {
					const next = new Set(prev);
					next.delete(note);
					return next;
				});
			}
		},
		[synthInstances, activeSynthId, getArp],
	);

	/** Trigger a noteOn on a specific synth instance by ID (for sequencer).
	 *  Routes through the per-synth arpeggiator when enabled.
	 */
	const synthNoteOn = useCallback(
		(synthId: string, note: number, velocity?: number) => {
			const arp = getArp(synthId);
			if (arp.currentParams.enabled) {
				arp.noteOn(note);
			} else {
				const synth = synthMapRef.current.get(synthId);
				if (synth?.isReady) {
					synth.noteOn(note, velocity);
				}
			}
		},
		[getArp],
	);

	/** Trigger a noteOff on a specific synth instance by ID (for sequencer).
	 *  Routes through the per-synth arpeggiator when enabled.
	 */
	const synthNoteOff = useCallback(
		(synthId: string, note: number) => {
			const arp = getArp(synthId);
			if (arp.currentParams.enabled) {
				arp.noteOff(note);
			} else {
				const synth = synthMapRef.current.get(synthId);
				if (synth?.isReady) {
					synth.noteOff(note);
				}
			}
		},
		[getArp],
	);

	/** Get a shareable URL encoding the current persisted state. */
	const getShareURL = useCallback(
		() => stateToShareURL(persistedRef.current),
		[],
	);

	const saveSequencerState = useCallback(
		(notes: PersistedSequencerNotes, measures: PersistedSequencerMeasures) => {
			persistedRef.current = saveSequencer(
				notes,
				measures,
				persistedRef.current,
			);
		},
		[],
	);

	/** Load a preset into the active synth — applies layers + delay + arp. */
	const loadPreset = useCallback(
		(preset: SynthPreset) => {
			const synth = synthRef.current;
			if (!synth?.isReady) return;

			// Assign unique IDs to preset layers to avoid collisions
			const layers = preset.layers.map((l, i) => ({
				...l,
				id: `layer-${i + 1}-${Date.now()}`,
			}));

			synth.setOscillatorLayers(layers);
			synth.setDelay(preset.delay);

			setParams((prev) => {
				const next = { ...prev, oscillatorLayers: layers };
				persistedRef.current = saveParams(next, persistedRef.current);
				return next;
			});
			setDelayParams(() => {
				persistedRef.current = saveDelay(preset.delay, persistedRef.current);
				return preset.delay;
			});

			// Apply arp settings if included in the preset
			if (preset.arp) {
				const arp = getArp(activeSynthId);
				arp.setParams(preset.arp);
				setArpParamsState(preset.arp);
				persistedRef.current = saveArp(preset.arp, persistedRef.current);
			}

			trace("hook", "loadPreset", { presetId: preset.id, name: preset.name });
		},
		[activeSynthId, getArp],
	);

	/** Randomize all synth parameters within musically useful ranges. */
	const randomizeParams = useCallback(() => {
		const synth = synthRef.current;
		if (!synth?.isReady) return;

		const waveforms: readonly WaveformType[] = [
			"sine",
			"sawtooth",
			"square",
			"triangle",
		];
		const rand = (min: number, max: number) =>
			Math.random() * (max - min) + min;
		const randInt = (min: number, max: number) =>
			Math.floor(rand(min, max + 1));
		const pick = <T,>(arr: readonly T[]): T =>
			arr[Math.floor(Math.random() * arr.length)] as T;

		const layerCount = randInt(1, 4);
		const layers: OscillatorLayer[] = [];
		for (let i = 0; i < layerCount; i++) {
			layers.push({
				id: `layer-${i + 1}-${Date.now()}`,
				waveform: pick(waveforms),
				semitoneOffset: pick([0, 0, 0, -12, 12, -7, 7]),
				centOffset: randInt(-15, 15),
				gain: rand(0.3, 1.0),
				pan: rand(-0.8, 0.8),
				adsr: {
					attack: rand(0.001, 1.5),
					decay: rand(0.05, 1.5),
					sustain: rand(0.1, 0.9),
					release: rand(0.05, 2.5),
				},
				filter: {
					cutoff: rand(200, 12000),
					resonance: rand(0.1, 8),
				},
				enabled: true,
			});
		}

		synth.setOscillatorLayers(layers);

		// Randomize delay
		const delayEnabled = Math.random() > 0.4;
		const randomDelay: DelayParams = {
			enabled: delayEnabled,
			timeMs: rand(100, 600),
			noteDivision: pick([
				"1/4",
				"1/8",
				"1/8d",
				"1/16",
				"1/4t",
				"1/8t",
			] as const),
			timeMode: pick(["free", "sync"] as const),
			feedback: rand(0.1, 0.6),
			mix: rand(0.1, 0.4),
			pingPong: Math.random() > 0.5,
			filterCutoff: rand(2000, 12000),
		};
		synth.setDelay(randomDelay);

		setParams((prev) => {
			const next = { ...prev, oscillatorLayers: layers };
			persistedRef.current = saveParams(next, persistedRef.current);
			return next;
		});
		setDelayParams(() => {
			persistedRef.current = saveDelay(randomDelay, persistedRef.current);
			return randomDelay;
		});

		trace("hook", "randomizeParams", { layerCount });
	}, []);

	/** Export current synth config as a downloadable JSON file. */
	const exportPreset = useCallback(() => {
		const preset: SynthPreset = {
			id: `custom-${Date.now()}`,
			name:
				synthInstances.find((s) => s.id === activeSynthId)?.name ?? "Custom",
			category: "custom",
			description: "User-exported preset",
			layers: params.oscillatorLayers,
			delay: delayParams,
			arp: arpParams,
		};
		const json = JSON.stringify(preset, null, 2);
		const blob = new Blob([json], { type: "application/json" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${preset.name.replace(/\s+/g, "-").toLowerCase()}-preset.json`;
		a.click();
		URL.revokeObjectURL(url);
		trace("hook", "exportPreset", { name: preset.name });
	}, [
		params.oscillatorLayers,
		delayParams,
		arpParams,
		synthInstances,
		activeSynthId,
	]);

	/** Import a synth preset from a JSON file. */
	const importPreset = useCallback(
		(file: File) => {
			const reader = new FileReader();
			reader.onload = () => {
				try {
					const data = JSON.parse(reader.result as string) as SynthPreset;
					if (!data.layers || !Array.isArray(data.layers)) {
						console.error("Invalid preset file: missing layers");
						return;
					}
					// Assign fresh layer IDs to avoid collisions
					const preset: SynthPreset = {
						id: data.id ?? `imported-${Date.now()}`,
						name: data.name ?? "Imported",
						category: data.category ?? "custom",
						description: data.description ?? "Imported preset",
						layers: data.layers.map((l: OscillatorLayer, i: number) => ({
							...l,
							id: `layer-${i + 1}-${Date.now()}`,
						})),
						delay: data.delay ?? DEFAULT_DELAY,
						arp: data.arp,
					};
					loadPreset(preset);
					trace("hook", "importPreset", { name: preset.name });
				} catch (e) {
					console.error("Failed to parse preset file:", e);
				}
			};
			reader.readAsText(file);
		},
		[loadPreset],
	);

	const value: SynthContextValue = useMemo(
		() => ({
			synth: synthRef.current,
			audioContext: audioEnvRef.current?.context ?? null,
			isReady,
			isLoading,
			error,
			params,
			activeNotes,
			tuning,
			adsrLinked,
			filterLinked,
			masterAnalyser: masterBusAnalyserRef.current,
			init,
			noteOn,
			noteOff,
			setADSR,
			setFilter,
			setMasterBusGain,
			masterBusGain: masterBusGainValue,
			setTempo,
			setTuning,
			setOscillatorLayers,
			updateLayer,
			updateLayerADSR,
			updateLayerFilter,
			setAdsrLinked,
			setFilterLinked,
			setDelay,
			delayParams,
			setLimiter,
			limiterParams,
			arpParams,
			setArp,
			chorusParams,
			setChorus,
			allNotesOff,
			panic,
			synthInstances,
			activeSynthId,
			addSynth,
			removeSynth,
			selectSynth,
			renameSynth,
			setSynthMidiChannel,
			setSynthVolume,
			setSynthPan,
			setSynthMuted,
			setSynthSolo,
			midiNoteOn,
			midiNoteOff,
			synthNoteOn,
			synthNoteOff,
			getShareURL,
			loadPreset,
			initialSequencerNotes: persistedRef.current.sequencerNotes,
			initialSequencerMeasures: persistedRef.current.sequencerMeasures,
			saveSequencerState,
			randomizeParams,
			exportPreset,
			importPreset,
		}),
		[
			isReady,
			isLoading,
			error,
			params,
			activeNotes,
			tuning,
			adsrLinked,
			filterLinked,
			init,
			noteOn,
			noteOff,
			setADSR,
			setFilter,
			setMasterBusGain,
			masterBusGainValue,
			setTempo,
			setTuning,
			setOscillatorLayers,
			updateLayer,
			updateLayerADSR,
			updateLayerFilter,
			setAdsrLinked,
			setFilterLinked,
			setDelay,
			delayParams,
			setLimiter,
			limiterParams,
			arpParams,
			setArp,
			chorusParams,
			setChorus,
			allNotesOff,
			panic,
			synthInstances,
			activeSynthId,
			addSynth,
			removeSynth,
			selectSynth,
			renameSynth,
			setSynthMidiChannel,
			setSynthVolume,
			setSynthPan,
			setSynthMuted,
			setSynthSolo,
			midiNoteOn,
			midiNoteOff,
			synthNoteOn,
			synthNoteOff,
			getShareURL,
			loadPreset,
			saveSequencerState,
			randomizeParams,
			exportPreset,
			importPreset,
		],
	);

	return (
		<SynthContext.Provider value={value}>{children}</SynthContext.Provider>
	);
}
